import { Bot, MoreHorizontal, Send } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { ApiError } from '../../api/errors';
import { useActiveUsers, useUserMap } from '../../api/org';
import { useAddComment, useComments, useEditComment } from '../../api/tasks';
import type { Comment, Task } from '../../api/types';
import { useSession } from '../../app/session';
import { Avatar } from '../../components/ui/Avatar';
import { Badge } from '../../components/ui/Badge';
import { Button, IconButton } from '../../components/ui/Button';
import { ConfirmDialog } from '../../components/ui/Dialog';
import { Checkbox, FormAlert } from '../../components/ui/Field';
import { Menu, useMenu } from '../../components/ui/Menu';
import { type MentionUser, RichTextEditor, type RichTextHandle, RichTextView } from '../../components/ui/RichText';
import { Skeleton } from '../../components/ui/Skeleton';
import { ErrorState } from '../../components/ui/States';
import { useToast } from '../../components/ui/Toast';
import { cn } from '../../lib/cn';
import { formatDateTime, formatRelative } from '../../lib/dates';
import type { MentionRef } from '../../lib/mentions';
import { MOD } from '../../lib/platform';
import { describeSensitive, detectSensitive, maskSensitive } from '../../lib/sensitive';

const LEGACY_MENTION = /@\[([^\]]*)\]\(([0-9a-f-]{36})\)/gi;

/** Текст коментаря з форматуванням (той самий формат, що й в описі задачі) і підсвіченими згадками. */
export function CommentBody({ body, mentioned = [] }: { body: string; mentioned?: MentionRef[] }) {
  const { markdown, names } = useMemo(() => {
    const extra: string[] = [];
    // Давній запис згадки «@[Ім’я](ідентифікатор)» показуємо як звичайне «@Ім’я».
    const md = body.replace(LEGACY_MENTION, (_m, name: string) => {
      extra.push(name);
      return `@${name}`;
    });
    return { markdown: md, names: [...new Set([...mentioned.map((m) => m.name), ...extra])].filter(Boolean) };
  }, [body, mentioned]);
  return <RichTextView markdown={markdown} names={names} />;
}

/** Колеги, яких можна згадати: активні працівники, крім себе. */
function useMentionUsers(): MentionUser[] {
  const users = useActiveUsers();
  const { userId } = useSession();
  return useMemo(() => users.filter((u) => u.id !== userId).map((u) => ({ id: u.id, name: u.name, avatarUrl: u.avatarUrl })), [users, userId]);
}

/** Ідентифікатори згаданих: ті, чиє «@Ім’я» є в тексті (сервер отримує перелік, а в тексті лишається ім’я). */
const mentionIds = (markdown: string, users: MentionUser[]) => users.filter((u) => markdown.includes(`@${u.name}`)).map((u) => u.id);

function CommentItem({ c, taskId }: { c: Comment; taskId: string }) {
  const users = useUserMap();
  const { userId, isOwner } = useSession();
  const author = c.authorId ? users.get(c.authorId) : undefined;
  const menu = useMenu();
  const edit = useEditComment(taskId);
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const editor = useRef<RichTextHandle>(null);
  const mentionUsers = useMentionUsers();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const saveEdit = () => {
    const md = (editor.current?.getMarkdown() ?? '').trim();
    if (!md) return;
    edit.mutate(
      { id: c.id, body: md },
      {
        onSuccess: () => setEditing(false),
        onError: (err) => toast.error('Коментар не змінено', err instanceof ApiError ? err.detail : undefined),
      },
    );
  };
  const own = c.kind === 'comment' && !c.deletedAt && (c.authorId === userId || isOwner);

  if (c.kind === 'system') {
    return (
      <li className="flex gap-3 py-2 pl-1 text-meta text-fg-3">
        <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-line-strong" aria-hidden />
        <span>
          {c.body} <span className="whitespace-nowrap">· {formatRelative(c.createdAt)}</span>
        </span>
      </li>
    );
  }

  return (
    <li className="group/c flex gap-3 py-3">
      <Avatar name={author?.name ?? 'ЄРС'} src={author?.avatarUrl} size={28} className="mt-0.5" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-meta font-semibold text-fg">{author?.name ?? 'ЄРС (автоматично)'}</span>
          <time dateTime={c.createdAt} title={formatDateTime(c.createdAt)} className="text-caption text-fg-3">
            {formatRelative(c.createdAt)}
          </time>
          {c.editedAt && !c.deletedAt && <span className="text-caption text-fg-3">змінено</span>}
          {c.kind === 'result' && <Badge tone="success">Результат</Badge>}
          {c.kind === 'reason' && <Badge tone="neutral">Причина зміни</Badge>}
          {c.createdByAgent && (
            <Badge tone="info" icon={<Bot className="size-3" aria-hidden />}>
              Створено агентом {c.createdByAgent}
            </Badge>
          )}
          {own && !editing && (
            <span className="ml-auto opacity-100 transition-opacity sm:opacity-0 sm:group-hover/c:opacity-100 sm:focus-within:opacity-100">
              <IconButton label="Дії з коментарем" size="sm" {...menu.triggerProps}>
                <MoreHorizontal className="size-4" aria-hidden />
              </IconButton>
            </span>
          )}
        </div>
        {c.deletedAt ? (
          <p className="mt-0.5 text-body text-fg-3 italic">Коментар видалено</p>
        ) : editing ? (
          <div className="mt-1.5 flex flex-col gap-2">
            <div className="rounded-md border border-line-input bg-surface focus-within:border-focus focus-within:ring-2 focus-within:ring-focus/20">
              <RichTextEditor
                handle={editor}
                value={c.body}
                onSubmit={saveEdit}
                label="Текст коментаря"
                users={mentionUsers}
                toolbar="always"
                bare
                minHeight={64}
                className="gap-0 pb-1.5 [&>[role=toolbar]]:px-1.5"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
                Скасувати
              </Button>
              <Button size="sm" variant="primary" loading={edit.isPending} onClick={saveEdit}>
                Зберегти
              </Button>
            </div>
          </div>
        ) : (
          <div className="mt-0.5">
            <CommentBody body={c.body} mentioned={c.mentions.map((id) => ({ id, name: users.get(id)?.name ?? '' })).filter((m) => m.name)} />
          </div>
        )}
      </div>
      <Menu
        open={menu.open}
        onClose={menu.close}
        anchor={menu.anchor}
        label="Дії з коментарем"
        placement="bottom-end"
        items={[
          { key: 'edit', label: 'Змінити', onSelect: () => setEditing(true) },
          { key: 'delete', label: 'Видалити…', tone: 'danger', onSelect: () => setConfirmDelete(true) },
        ]}
      />
      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title="Видалити коментар?"
        description="Текст буде приховано, у стрічці залишиться позначка «Коментар видалено»."
        confirmLabel="Видалити"
        tone="danger"
        loading={edit.isPending}
        onConfirm={() => edit.mutate({ id: c.id, body: null }, { onSuccess: () => setConfirmDelete(false) })}
      />
    </li>
  );
}

export function CommentList({ task }: { task: Task }) {
  const q = useComments(task.id);
  if (q.isLoading)
    return (
      <div className="flex flex-col gap-4 py-3" role="status" aria-label="Завантаження коментарів">
        {[0, 1].map((i) => (
          <div key={i} className="flex gap-3">
            <Skeleton className="size-7 rounded-full" />
            <div className="flex flex-1 flex-col gap-2">
              <Skeleton className="h-3 w-40" />
              <Skeleton className="h-3 w-4/5" />
            </div>
          </div>
        ))}
      </div>
    );
  if (q.error) return <ErrorState error={q.error} onRetry={() => void q.refetch()} compact />;
  const list = q.data ?? [];
  if (!list.length) return <p className="py-4 text-meta text-fg-3">Коментарів ще немає. Питання, рішення й домовленості фіксуйте тут, а не в месенджері.</p>;
  return (
    <ul className="divide-y divide-line" aria-label="Коментарі">
      {list.map((c) => (
        <CommentItem key={c.id} c={c} taskId={task.id} />
      ))}
    </ul>
  );
}

/**
 * Поле коментаря: один рядок, доки ним не користуються, — щоб не забирати висоту картки; розкривається під час
 * фокуса й лишається розкритим, поки є текст. Форматування — як в описі задачі; ⌘/Ctrl+Enter надсилає.
 */
export function CommentComposer({ task }: { task: Task }) {
  const add = useAddComment(task.id);
  const toast = useToast();
  const mentionUsers = useMentionUsers();
  const editor = useRef<RichTextHandle>(null);
  const [text, setText] = useState('');
  const [focused, setFocused] = useState(false);
  const [allowSensitive, setAllowSensitive] = useState(false);

  const sensitive = detectSensitive(text);
  const serverError = add.error instanceof ApiError ? add.error : null;
  const expanded = focused || !!text.trim() || add.isPending || !!serverError;

  const clear = () => {
    editor.current?.setMarkdown('');
    setText('');
    setAllowSensitive(false);
  };

  const send = (force = false) => {
    const md = (editor.current?.getMarkdown() ?? text).trim();
    if (!md) return;
    if (detectSensitive(md).length && !force && !allowSensitive) return;
    const ids = mentionIds(md, mentionUsers);
    add.mutate(
      { body: md, mentions: ids, allowSensitive: force || allowSensitive || undefined },
      {
        onSuccess: () => {
          clear();
          if (ids.length) toast.success('Коментар надіслано', `Згадані колеги отримають сповіщення (${ids.length}).`);
        },
        onError: (e) => {
          // Немає мережі: коментар у черзі застосунку — поле очищаємо, повідомлення покаже оболонка.
          if (e instanceof ApiError && e.isQueued) {
            clear();
            add.reset();
          }
        },
      },
    );
  };

  const mask = () => {
    const masked = maskSensitive(editor.current?.getMarkdown() ?? text);
    editor.current?.setMarkdown(masked);
    setText(masked);
  };

  return (
    <div className="flex flex-col gap-2">
      <div
        onFocus={() => setFocused(true)}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocused(false);
        }}
        className="rounded-md border border-line-input bg-surface focus-within:border-focus focus-within:ring-2 focus-within:ring-focus/20"
      >
        <RichTextEditor
          handle={editor}
          value=""
          onChange={setText}
          onSubmit={() => send()}
          label="Новий коментар"
          placeholder={expanded ? 'Коментар… Згадайте колегу через @' : 'Написати коментар…'}
          users={mentionUsers}
          toolbar={expanded ? 'always' : 'focus'}
          bare
          minHeight={expanded ? 76 : 34}
          className={cn('gap-0', expanded && 'pb-1.5 [&>[role=toolbar]]:px-1.5')}
          toolbarEnd={
            <>
              <span className="hidden sm:inline">
                <kbd className="key">{MOD}</kbd>+<kbd className="key">Enter</kbd> — надіслати
              </span>
              <Button
                size="sm"
                variant="primary"
                icon={<Send className="size-3.5" aria-hidden />}
                loading={add.isPending}
                disabled={!text.trim() || (sensitive.length > 0 && !allowSensitive)}
                onClick={() => send()}
              >
                Надіслати
              </Button>
            </>
          }
        />
      </div>
      {sensitive.length > 0 && (
        <FormAlert tone="warning" title="Схоже, у коментарі є чутливі дані" detail={`Виявлено: ${describeSensitive(sensitive)}. Паролі й реквізити не зберігають у коментарях — замаскуйте їх.`}>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <Button size="sm" variant="secondary" onClick={mask}>
              Замаскувати
            </Button>
            <Checkbox checked={allowSensitive} onChange={setAllowSensitive} label="Надіслати як є" />
          </div>
        </FormAlert>
      )}
      {serverError && (
        <FormAlert tone={serverError.isSensitive ? 'warning' : 'danger'} detail={serverError.detail}>
          {serverError.isSensitive && (
            <div className="mt-2 flex gap-2">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  mask();
                  add.reset();
                }}
              >
                Замаскувати
              </Button>
              <Button size="sm" variant="ghost" onClick={() => send(true)}>
                Надіслати як є
              </Button>
            </div>
          )}
        </FormAlert>
      )}
    </div>
  );
}
